/**
 * API service for configurable Work Order Attributes
 * Admins define per-company identity fields for work orders (e.g. Job Order #,
 * VIN for a vehicle upfitter; Batch #, Lot Code for a pharma customer).
 * Work orders carry the values as a JSON string (WorkOrder.attributeValues)
 * keyed by attrKey.
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/WorkOrderAttributes`;

export type AttributeDataType = "text" | "number" | "date" | "enum";
export type AttributeDisplaySlot = "headline" | "identifier" | "chip" | "detail";

export interface WorkOrderAttributeDefinition {
	id: number;
	companyId: number;
	attrKey: string;
	label: string;
	dataType: AttributeDataType;
	/** JSON array of strings when dataType is "enum" */
	enumOptions?: string;
	validationRegex?: string;
	isRequired: boolean;
	isSearchable: boolean;
	displaySlot: AttributeDisplaySlot;
	sortOrder: number;
	isActive: boolean;
	createdDate: string;
	modifiedDate?: string;
}

export interface CreateWorkOrderAttributeDefinitionDto {
	companyId: number;
	attrKey: string;
	label: string;
	dataType: AttributeDataType;
	enumOptions?: string;
	validationRegex?: string;
	isRequired: boolean;
	isSearchable: boolean;
	displaySlot: AttributeDisplaySlot;
	sortOrder: number;
}

export interface UpdateWorkOrderAttributeDefinitionDto extends CreateWorkOrderAttributeDefinitionDto {
	id: number;
	isActive: boolean;
}

export interface AttributeTemplateDefinition {
	attrKey: string;
	label: string;
	dataType: AttributeDataType;
	enumOptions?: string;
	validationRegex?: string;
	isRequired: boolean;
	isSearchable: boolean;
	displaySlot: AttributeDisplaySlot;
	sortOrder: number;
}

export interface AttributeTemplate {
	key: string;
	name: string;
	description: string;
	definitions: AttributeTemplateDefinition[];
}

export interface ApplyTemplateResult {
	message: string;
	addedCount: number;
	skippedCount: number;
}

export interface ValidateAttributeValuesResult {
	isValid: boolean;
	errors: string[];
}

/** Parse a WorkOrder.attributeValues JSON string into a safe record */
export const parseAttributeValues = (json?: string | null): Record<string, string> => {
	if (!json) return {};
	try {
		const parsed = JSON.parse(json);
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			const result: Record<string, string> = {};
			Object.keys(parsed).forEach((key) => {
				if (parsed[key] !== null && parsed[key] !== undefined) {
					result[key] = String(parsed[key]);
				}
			});
			return result;
		}
		return {};
	} catch {
		return {};
	}
};

/** Parse a definition's enumOptions JSON string into a string array */
export const parseEnumOptions = (json?: string | null): string[] => {
	if (!json) return [];
	try {
		const parsed = JSON.parse(json);
		return Array.isArray(parsed) ? parsed.map(String) : [];
	} catch {
		return [];
	}
};

export const workOrderAttributesApi = {
	/**
	 * Get attribute definitions for a company
	 */
	getByCompany: async (
		companyId: number,
		includeInactive = false
	): Promise<WorkOrderAttributeDefinition[]> => {
		const response = await axios.get<WorkOrderAttributeDefinition[]>(API_BASE_URL, {
			params: { companyId, includeInactive },
		});
		return response.data;
	},

	/**
	 * Get a single attribute definition by ID
	 */
	getById: async (id: number): Promise<WorkOrderAttributeDefinition> => {
		const response = await axios.get<WorkOrderAttributeDefinition>(`${API_BASE_URL}/${id}`);
		return response.data;
	},

	/**
	 * Create a new attribute definition
	 */
	create: async (
		definition: CreateWorkOrderAttributeDefinitionDto
	): Promise<WorkOrderAttributeDefinition> => {
		const response = await axios.post<WorkOrderAttributeDefinition>(API_BASE_URL, definition);
		return response.data;
	},

	/**
	 * Update an existing attribute definition
	 */
	update: async (
		id: number,
		definition: UpdateWorkOrderAttributeDefinitionDto
	): Promise<void> => {
		await axios.put(`${API_BASE_URL}/${id}`, definition);
	},

	/**
	 * Delete an attribute definition
	 */
	delete: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/**
	 * Get the built-in industry templates (definition sets)
	 */
	getTemplates: async (): Promise<AttributeTemplate[]> => {
		const response = await axios.get<AttributeTemplate[]>(`${API_BASE_URL}/templates`);
		return response.data;
	},

	/**
	 * Apply a built-in template to a company (adds only missing keys)
	 */
	applyTemplate: async (companyId: number, templateKey: string): Promise<ApplyTemplateResult> => {
		const response = await axios.post<ApplyTemplateResult>(`${API_BASE_URL}/apply-template`, {
			companyId,
			templateKey,
		});
		return response.data;
	},

	/**
	 * Validate a dictionary of attribute values against the company's definitions
	 */
	validate: async (
		companyId: number,
		values: Record<string, string>
	): Promise<ValidateAttributeValuesResult> => {
		const response = await axios.post<ValidateAttributeValuesResult>(`${API_BASE_URL}/validate`, {
			companyId,
			values,
		});
		return response.data;
	},
};

export default workOrderAttributesApi;
